import { Select } from '@/components/ui/select';
import { getTenantBrandColor, useTenant } from '@/hooks/useTenant';

function getTenantContextLabel(mode: 'all' | 'tenant' | 'assigned', tenantName?: string): string {
  if (mode === 'all') {
    return 'All tenants';
  }

  if (tenantName) {
    return tenantName;
  }

  return mode === 'assigned' ? 'Assigned tenant' : 'Tenant not selected';
}

export function TenantSwitcher() {
  const {
    isAdmin,
    isStaff,
    mode,
    activeTenantId,
    activeTenant,
    brandColor,
    tenantList,
    setMode,
    setActiveTenantId,
  } = useTenant();

  const contextLabel = getTenantContextLabel(mode, activeTenant?.name);

  if (!isAdmin && !isStaff) {
    return null;
  }

  if (isStaff) {
    const staffColor = getTenantBrandColor(activeTenant);
    return (
      <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/60 border border-border">
        {staffColor && (
          <span
            className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: staffColor }}
          />
        )}
        <span className="text-xs font-medium text-muted-foreground">Tenant</span>
        <span className="text-xs font-semibold text-foreground truncate max-w-[180px]">
          {contextLabel}
        </span>
      </div>
    );
  }

  const hasTenants = tenantList.length > 0;

  const handleModeChange = (nextMode: string) => {
    if (nextMode === 'all') {
      setMode('all');
      setActiveTenantId(null);
      return;
    }

    setMode('tenant');
    if (!activeTenantId && hasTenants) {
      setActiveTenantId(tenantList[0].id);
    }
  };

  const handleTenantChange = (tenantId: string) => {
    setActiveTenantId(tenantId || null);
  };

  return (
    <div className="hidden md:flex items-center gap-2">
      <Select
        value={mode === 'all' ? 'all' : 'tenant'}
        onChange={event => handleModeChange(event.target.value)}
        className="h-9 min-w-[132px]"
        data-testid="tenant-mode-select"
      >
        <option value="all">All tenants</option>
        <option value="tenant">Selected tenant</option>
      </Select>

      <Select
        value={activeTenantId ?? ''}
        onChange={event => handleTenantChange(event.target.value)}
        disabled={mode === 'all' || !hasTenants}
        className="h-9 min-w-[180px]"
        data-testid="tenant-select"
      >
        {!hasTenants && <option value="">No tenants available</option>}
        {hasTenants && <option value="">Select tenant</option>}
        {tenantList.map(tenant => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.name}
          </option>
        ))}
      </Select>

      <span className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1.5">
        {brandColor && (
          <span
            className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: brandColor }}
          />
        )}
        {contextLabel}
      </span>
    </div>
  );
}
